import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import clsx from 'clsx';
import ReactDOM from 'react-dom';

// Import components
import ViewerHeader from './components/ViewerHeader';
import SearchBar from './components/SearchBar';
import StateContent from './components/StateContent';
import ResizeHandle from './components/ResizeHandle';
import PathDisplay from './components/PathDisplay';
import EmptyStateView from './components/EmptyStateView';
import ContextMenu from './components/ContextMenu';

// Import contexts
import { ContextMenuProvider, useContextMenu } from './contexts/ContextMenuContext';

// Import utilities and hooks
import { sortStateObject } from './utils/sortUtils';
import { sectionContainsMatch } from './utils/searchUtils';
import { useDebounce } from './hooks/useDebounce';
import { useResizable } from './hooks/useResizable';
import { useDraggable } from './hooks/useDraggable';
import { useStateSearch } from './hooks/useStateSearch';
import { useMergeRefs } from 'flow/hooks/useMergeRefs';

// Define interfaces for section items and component state
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Main StateViewer component for viewing Redux state
 * @returns {JSX.Element} - Rendered component
 */
var StateViewer = function StateViewer() {
  // Access the Redux state
  var state = useAppSelector(function (s) {
    return s;
  });

  // Basic state
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    isMinimized = _useState2[0],
    setIsMinimized = _useState2[1];
  var _useState3 = useState('right'),
    _useState4 = _slicedToArray(_useState3, 2),
    maximizedPosition = _useState4[0],
    setMaximizedPosition = _useState4[1];
  var _useState5 = useState({}),
    _useState6 = _slicedToArray(_useState5, 2),
    expandedSections = _useState6[0],
    setExpandedSections = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    inPopup = _useState8[0],
    setInPopup = _useState8[1];

  // Sort the state before rendering
  var sortedState = useMemo(function () {
    return sortStateObject(state);
  }, [state]);

  // Path tracking state
  var _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    hoveredPath = _useState0[0],
    setHoveredPath = _useState0[1];
  var debouncedHoveredPath = useDebounce(hoveredPath, 50);
  var isHovering = useRef(false);

  // Use custom hooks for different functionality
  var _useResizable = useResizable(40, isMinimized, maximizedPosition),
    viewerWidth = _useResizable.viewerWidth,
    isResizing = _useResizable.isResizing,
    resizeRef = _useResizable.elementRef,
    handleResizeStart = _useResizable.handleResizeStart;
  var _useDraggable = useDraggable(isMinimized),
    position = _useDraggable.position,
    isDragging = _useDraggable.isDragging,
    dragPosition = _useDraggable.dragPosition,
    dragRef = _useDraggable.elementRef,
    handleMouseDown = _useDraggable.handleMouseDown;
  var _useStateSearch = useStateSearch(sortedState, setExpandedSections),
    inputValue = _useStateSearch.inputValue,
    debouncedSearchTerm = _useStateSearch.debouncedSearchTerm,
    searchResults = _useStateSearch.searchResults,
    matchingPaths = _useStateSearch.matchingPaths,
    handleSearchInputChange = _useStateSearch.handleSearchInputChange,
    clearSearch = _useStateSearch.clearSearch,
    parseSearchPath = _useStateSearch.parseSearchPath;

  // Combine refs to use both resize and drag functionality
  var viewerRef = useMergeRefs(resizeRef, dragRef);

  // Toggle the position of the maximized viewer between left and right
  var toggleMaximizedPosition = useCallback(function () {
    setMaximizedPosition(function (prev) {
      return prev === 'right' ? 'left' : 'right';
    });
  }, []);

  // Toggle minimized state
  var toggleMinimize = useCallback(function () {
    setIsMinimized(function (prev) {
      return !prev;
    });
  }, []);

  // Function to collapse all sections and clear search
  var collapseAll = useCallback(function () {
    setExpandedSections({});
    clearSearch();
  }, [clearSearch]);

  // Function to toggle section expansion
  var toggleSection = useCallback(function (key) {
    setExpandedSections(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, key, !prev[key]));
    });
  }, []);

  // Mouse enter/leave handlers for the entire viewer
  var handleViewerMouseEnter = useCallback(function () {
    isHovering.current = true;
  }, []);
  var handleViewerMouseLeave = useCallback(function () {
    isHovering.current = false;
    setHoveredPath('');
  }, []);

  // Wrapper for search input change to handle string input and event properly
  var handleSearchChange = useCallback(function (input) {
    handleSearchInputChange(input);
  }, [handleSearchInputChange]);

  // Calculate sections to render with memoization for performance
  var sectionsToRender = useMemo(function () {
    if (!debouncedSearchTerm) {
      // When no search term, show all sections
      return Object.entries(sortedState).map(function (_ref) {
        var _ref2 = _slicedToArray(_ref, 2),
          key = _ref2[0],
          value = _ref2[1];
        return {
          key: key,
          value: value,
          isMatch: false,
          isExpanded: expandedSections[key]
        };
      });
    }
    var searchPath = parseSearchPath(debouncedSearchTerm);
    return Object.entries(sortedState).filter(function (_ref3) {
      var _ref4 = _slicedToArray(_ref3, 2),
        key = _ref4[0],
        value = _ref4[1];
      return sectionContainsMatch(key, value, searchPath);
    }).map(function (_ref5) {
      var _ref6 = _slicedToArray(_ref5, 2),
        key = _ref6[0],
        value = _ref6[1];
      var isKeyMatch = key.toLowerCase().includes(searchPath.toLowerCase());
      var mightContainValue = true; // simplified for brevity
      var isIdSearch = searchPath.includes('-') || /^\d+$/.test(searchPath);
      var mightContainId = isIdSearch && ['chats', 'messages', 'users', 'contacts', 'conversations'].includes(key.toLowerCase());
      var isSearchPathStart = searchPath.split('.')[0] === key.toLowerCase();

      // Always expand the top-level section that matches the search
      var isExpanded = expandedSections[key] || isKeyMatch || isSearchPathStart || mightContainId;
      return {
        key: key,
        value: value,
        isMatch: isKeyMatch || mightContainValue || mightContainId,
        isExpanded: isExpanded
      };
    });
  }, [sortedState, debouncedSearchTerm, expandedSections, parseSearchPath]);

  // Get the context menu functionality - add openContextMenu
  var _useContextMenu = useContextMenu(),
    activeContextMenu = _useContextMenu.activeContextMenu,
    closeAllContextMenus = _useContextMenu.closeAllContextMenus,
    openContextMenu = _useContextMenu.openContextMenu;

  // Add this handler for right-click events on the StateViewer
  var handleContextMenu = useCallback(function (e) {
    e.preventDefault();
    e.stopPropagation();
    var menuItems = [{
      label: 'Collapse All',
      action: collapseAll
    }, {
      label: "Toggle Position (".concat(maximizedPosition, ")"),
      action: toggleMaximizedPosition
    }, {
      label: isMinimized ? 'Maximize' : 'Minimize',
      action: toggleMinimize
    }, {
      label: 'Clear Search',
      action: clearSearch
    }];
    openContextMenu({
      x: e.clientX,
      y: e.clientY,
      menuItems: menuItems
    });
  }, [collapseAll, toggleMaximizedPosition, toggleMinimize, isMinimized, clearSearch, maximizedPosition, openContextMenu]);

  // Open popup and render portal
  var popupWindowRef = useRef(null);
  var popupContainerRef = useRef(null);
  var openInPopup = useCallback(function () {
    if (popupWindowRef.current && !popupWindowRef.current.closed) {
      popupWindowRef.current.focus();
      return;
    }
    var popup = window.open('', 'StateViewerPopup', 'width=900,height=800,left=200,top=100');
    if (!popup) return;
    popup.document.title = 'State Viewer';
    // Inject main.css for Tailwind and app styles
    // Try to find the main stylesheet from the current document
    // Copy all <style> elements from the current document to the popup
    Array.from(document.querySelectorAll('style')).forEach(function (style) {
      var newStyle = popup.document.createElement('style');
      newStyle.textContent = style.textContent;
      popup.document.head.appendChild(newStyle);
    });

    // Create container for portal
    var container = popup.document.createElement('div');
    popup.document.body.appendChild(container);
    popupContainerRef.current = container;
    popupWindowRef.current = popup;
    setInPopup(true);
    // Cleanup when popup closes
    var timer = setInterval(function () {
      if (popup.closed) {
        setInPopup(false);
        popupWindowRef.current = null;
        popupContainerRef.current = null;
        clearInterval(timer);
      }
    }, 500);
  }, []);

  // Cleanup on unmount
  useEffect(function () {
    return function () {
      if (popupWindowRef.current && !popupWindowRef.current.closed) {
        popupWindowRef.current.close();
      }
    };
  }, []);

  // The actual viewer content
  var viewerContent = /*#__PURE__*/_jsxs("div", {
    ref: viewerRef,
    className: clsx('fixed z-[99999] text-black', isMinimized ? 'cursor-grab' : 'h-screen overflow-auto bg-white font-mono shadow-lg', isMinimized && !isDragging && position.x, isMinimized && !isDragging && position.y, !isMinimized && !inPopup && (maximizedPosition === 'right' ? 'end-0' : 'start-0'), !isMinimized && !inPopup && 'top-0', isResizing && 'cursor-ew-resize'),
    style: _objectSpread(_objectSpread({}, isDragging && isMinimized ? {
      position: 'fixed',
      left: "".concat(dragPosition.x, "px"),
      top: "".concat(dragPosition.y, "px"),
      cursor: 'grabbing'
    } : {}), !isMinimized ? inPopup ? {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      minWidth: 0
    } : {
      width: "".concat(viewerWidth, "vw"),
      minWidth: '400px'
    } : {}),
    onMouseEnter: handleViewerMouseEnter,
    onMouseLeave: handleViewerMouseLeave,
    onContextMenu: handleContextMenu,
    children: [/*#__PURE__*/_jsx(ViewerHeader, {
      isMinimized: isMinimized,
      maximizedPosition: maximizedPosition,
      toggleMaximizedPosition: toggleMaximizedPosition,
      collapseAll: collapseAll,
      toggleMinimize: toggleMinimize,
      onMouseDown: isMinimized ? handleMouseDown : undefined,
      onPopout: openInPopup
    }), !isMinimized && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(SearchBar, {
        searchTerm: inputValue,
        onSearchChange: handleSearchChange,
        results: sectionsToRender.length
      }), /*#__PURE__*/_jsx(StateContent, {
        sectionsToRender: sectionsToRender,
        debouncedSearchTerm: debouncedSearchTerm,
        toggleSection: toggleSection,
        parseSearchPath: parseSearchPath,
        searchResults: searchResults,
        matchingPaths: matchingPaths,
        setHoveredPath: setHoveredPath
      }), /*#__PURE__*/_jsx(ResizeHandle, {
        maximizedPosition: maximizedPosition,
        onMouseDown: handleResizeStart
      }), /*#__PURE__*/_jsx(PathDisplay, {
        isVisible: !isMinimized && debouncedHoveredPath && isHovering.current,
        debouncedHoveredPath: debouncedHoveredPath,
        viewerWidth: viewerWidth,
        maximizedPosition: maximizedPosition
      })]
    }), activeContextMenu && /*#__PURE__*/_jsx(ContextMenu, {
      x: activeContextMenu.x,
      y: activeContextMenu.y,
      menuItems: activeContextMenu.menuItems,
      onClose: closeAllContextMenus
    })]
  });

  // Handle the case where state is empty or invalid
  if (!state || Object.keys(state).length === 0) {
    return /*#__PURE__*/_jsx(EmptyStateView, {});
  }

  // Render in portal if inPopup, else normal
  if (inPopup && popupContainerRef.current) {
    return /*#__PURE__*/ReactDOM.createPortal(viewerContent, popupContainerRef.current);
  }
  return viewerContent;
};

// Properly wrap and export the component
var StateViewerWithProvider = function StateViewerWithProvider() {
  return /*#__PURE__*/_jsx(ContextMenuProvider, {
    children: /*#__PURE__*/_jsx(StateViewer, {})
  });
};
export default StateViewerWithProvider;